/**
 * Stories with live links, built to be SHARED (Insights find-and-share, 0.3.6).
 * One row per filing; the link is the owner masthead's copy, other mastheads'
 * copies appear as small secondary links. Copy link copies one URL; "Copy for
 * email" copies a plain-text list ready to paste into an email or chat.
 */
import { useState } from 'react';
import { Check, Copy, ExternalLink, Mail } from 'lucide-react';
import type { Story } from '../../lib/api/insights.js';

export const MASTHEAD_LABEL: Record<string, string> = {
  orecast: 'Orecast',
  marketfloor: 'The Market Floor',
  bellwire: 'Bell Wire',
};

function fmtDay(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function emailText(heading: string, stories: Story[], withTicker: boolean): string {
  const lines = stories.filter((s) => s.url).map((s) =>
    `• ${fmtDay(s.published_at)} — ${withTicker && s.ticker ? `${s.ticker}: ` : ''}${s.title ?? ''}\n  ${s.url}`);
  return `${heading}\n\n${lines.join('\n')}\n`;
}

function CopyButton({ text, label, icon }: { text: string; label: string; icon?: 'link' | 'mail' }) {
  const [done, setDone] = useState<null | boolean>(null);
  return (
    <button
      type="button"
      onClick={async () => { setDone(await copyText(text)); setTimeout(() => setDone(null), 1500); }}
      className="inline-flex items-center gap-1 rounded-full border border-grey-200 px-2.5 py-1 text-xs text-grey-700 hover:bg-grey-50"
    >
      {done ? <Check className="h-3.5 w-3.5 text-success-600" /> : icon === 'mail' ? <Mail className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {done === false ? 'Copy failed' : done ? 'Copied' : label}
    </button>
  );
}

export function CopyAllButton({ heading, stories, withTicker = false }: { heading: string; stories: Story[]; withTicker?: boolean }) {
  return <CopyButton text={emailText(heading, stories, withTicker)} label="Copy for email" icon="mail" />;
}

export function StoryList({ stories, showCompany = false, onCompany }: {
  stories: Story[];
  showCompany?: boolean;
  onCompany?: (entityId: string) => void;
}) {
  if (stories.length === 0) {
    return <p className="text-sm text-grey-400 py-6 text-center">No published articles in this window.</p>;
  }
  return (
    <ul className="divide-y divide-grey-100">
      {stories.map((s) => (
        <li key={s.story_id} className="py-3 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-4">
          <div className="text-xs text-grey-500 tabular-nums w-24 flex-shrink-0 pt-0.5">{fmtDay(s.published_at)}</div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              {showCompany && s.ticker && (
                <button type="button" onClick={() => onCompany?.(s.entity_id)}
                  className="rounded bg-grey-100 px-1.5 text-[11px] font-semibold text-grey-700 hover:bg-grey-200">{s.ticker}</button>
              )}
              {s.url ? (
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-grey-900 hover:underline">
                  {s.title ?? '(untitled)'}
                </a>
              ) : (
                <span className="text-sm text-grey-500">{s.title ?? '(untitled)'} <span className="text-[11px]">(not live)</span></span>
              )}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-grey-500">
              {s.masthead && <span>{MASTHEAD_LABEL[s.masthead] ?? s.masthead}</span>}
              {s.also_on.length > 0 && (
                <span>
                  · also on{' '}
                  {s.also_on.map((a, i) => (
                    <span key={a.url}>
                      {i > 0 && ', '}
                      <a href={a.url} target="_blank" rel="noopener noreferrer" className="hover:underline">{MASTHEAD_LABEL[a.masthead ?? ''] ?? a.masthead}</a>
                    </span>
                  ))}
                </span>
              )}
              {(s.views ?? 0) > 0 && <span title={s.views_exact ? 'Human page views across our copies — every page counted' : 'Human page views across our copies — some days counted the top 100 pages only, so a minimum'}>· {Math.round(s.views ?? 0).toLocaleString()}{s.views_exact ? '' : '+'} views</span>}
              {(s.ai_reads ?? 0) > 0 && <span title={`Times an AI assistant fetched this story because a person asked it something${s.ai_reads_estimated ? ' — some days estimated from a sample' : ''}`}>· {s.ai_reads_estimated ? '~' : ''}{Math.round(s.ai_reads ?? 0).toLocaleString()} AI-assisted reads</span>}
              {s.impressions > 0 && <span>· {Math.round(s.impressions).toLocaleString()} social impressions</span>}
              {(s.reach ?? 0) > 0 && <span title="LinkedIn unique members reached">· {Math.round(s.reach ?? 0).toLocaleString()} reached</span>}
              {(s.social_clicks ?? 0) > 0 && <span title="X link clicks (LinkedIn reports none)">· {Math.round(s.social_clicks ?? 0).toLocaleString()} clicks</span>}
              {(s.search_clicks ?? 0) > 0 && <span>· {Math.round(s.search_clicks ?? 0).toLocaleString()} Google clicks</span>}
            </div>
          </div>
          {s.url && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <CopyButton text={s.url} label="Copy link" />
              <a href={s.url} target="_blank" rel="noopener noreferrer" aria-label="Open article"
                className="inline-flex items-center rounded-full border border-grey-200 p-1.5 text-grey-600 hover:bg-grey-50">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
